/// <reference types="google.maps" />
import { AdvancedMarker, useMap, Marker as VisGLMarker } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import type Supercluster from "supercluster";
import type { Marker } from "./map-view";
import { MapViewClusterMarker } from "./map-view-cluster-marker";

interface MapViewClusterLayerProps {
	clusterIndex: Supercluster | null;
	markers?: Marker[];
	disableClustering?: boolean;
	clusterMaxZoom?: number;
}

export function MapViewClusterLayer({
	clusterIndex,
	markers,
	disableClustering,
	clusterMaxZoom = 20,
}: MapViewClusterLayerProps) {
	const map = useMap();
	const [clusters, setClusters] = useState<
		(
			| Supercluster.PointFeature<Supercluster.AnyProps>
			| Supercluster.ClusterFeature<Supercluster.AnyProps>
		)[]
	>([]);
	const [zoom, setZoom] = useState<number | null>(null);

	useEffect(() => {
		if (!map || !clusterIndex) return;

		function updateClusters() {
			if (!clusterIndex) return;
			if (!map) return;
			const bounds = map.getBounds();
			const currentZoom = map.getZoom();
			if (!bounds || currentZoom == null) return;
			const ne = bounds.getNorthEast();
			const sw = bounds.getSouthWest();
			const bbox: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
			const zoomLevel = Math.min(Math.round(currentZoom), clusterMaxZoom);
			const clusters = clusterIndex.getClusters(bbox, zoomLevel);
			setZoom(currentZoom);
			setClusters(clusters);
		}

		updateClusters();
		const idleListener = map.addListener("idle", updateClusters);
		return () => {
			google.maps.event.removeListener(idleListener);
		};
	}, [map, clusterIndex, clusterMaxZoom]);

	function renderClusterOrMarker(
		cluster:
			| Supercluster.PointFeature<Supercluster.AnyProps>
			| Supercluster.ClusterFeature<Supercluster.AnyProps>,
	) {
		const [lng, lat] = cluster.geometry.coordinates;
		if (!lng || !lat) return null;
		if (cluster.properties.cluster) {
			return (
				<AdvancedMarker
					key={`cluster-${cluster.id}`}
					position={{ lat, lng }}
					onClick={() => {
						if (!map || !clusterIndex) return;
						const expansionZoom = clusterIndex?.getClusterExpansionZoom(Number(cluster.id)!);
						if (expansionZoom && map) {
							map.setZoom(expansionZoom);
							map.panTo({ lat, lng });
						}
					}}
				>
					<MapViewClusterMarker cluster={cluster} />
				</AdvancedMarker>
			);
		}

		// Single point
		const original = markers?.[cluster.properties.index];
		return original?.render ? (
			<AdvancedMarker
				key={`marker-${cluster.properties.index}`}
				position={{ lat, lng }}
			>
				{original.render()}
			</AdvancedMarker>
		) : (
			<VisGLMarker
				key={`marker-${cluster.properties.index}`}
				position={{ lat, lng }}
			/>
		);
	}

	function renderPlainMarker(marker: Marker, i: number) {
		return marker.render ? (
			<AdvancedMarker
				key={`marker-${i}`}
				position={{ lat: marker.latitude, lng: marker.longitude }}
			>
				{marker.render()}
			</AdvancedMarker>
		) : (
			<VisGLMarker
				key={`marker-${i}`}
				position={{ lat: marker.latitude, lng: marker.longitude }}
			/>
		);
	}

	const isPastClusterThreshold = zoom != null && zoom >= clusterMaxZoom;

	if (disableClustering || isPastClusterThreshold) {
		return <>{markers?.map(renderPlainMarker)}</>;
	}

	const excludedMarkers = markers?.filter((m) => m.excludeFromClustering) ?? [];

	return (
		<>
			{clusters.map(renderClusterOrMarker)}
			{excludedMarkers.map((marker) => renderPlainMarker(marker, markers!.indexOf(marker)))}
		</>
	);
}
